import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  Me,
  Passkey,
  PasskeyList,
  PasskeyOptions,
  TokenResponse,
  type LoginBody,
  type RegisterBody,
  type UnitsPreference,
} from '@klunkers/shared'
import { api, ApiRequestError, setTokenProvider } from '@/api/http'
import {
  createPasskey,
  getPasskey,
  hasPasskeyHere,
  markPasskeyHere,
  PasskeyCancelled,
  passkeysSupported,
} from '@/native/passkeys'
import { getPreference, removePreference, setPreference } from '@/native/preferences'

const STORAGE_KEY = 'klunkers.session'

interface StoredSession {
  access_token: string
  refresh_token: string
  /** The profile as last loaded, so an offline start still knows who the rider is. */
  me?: Me
}

/** The user id inside an access token, without verifying it (the API does that). */
export function tokenSubject(token: string | null): string | null {
  if (!token) return null
  try {
    const payload = token.split('.')[1] ?? ''
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const sub = (JSON.parse(json) as { sub?: unknown }).sub
    return typeof sub === 'string' && sub.length > 0 ? sub : null
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const me = ref<Me | null>(null)
  const ready = ref(false)

  const isSignedIn = computed(() => accessToken.value !== null)
  const units = computed<UnitsPreference>(() => me.value?.units ?? 'metric')
  /**
   * Who is signed in, even before (or without) `/me` loading: the cached
   * profile, else the subject of the access token. Offline captures and the
   * per-user queue key on this, never on the network.
   */
  const userId = computed(() => me.value?.id ?? tokenSubject(accessToken.value))

  async function persist() {
    if (accessToken.value && refreshToken.value) {
      const session: StoredSession = {
        access_token: accessToken.value,
        refresh_token: refreshToken.value,
        ...(me.value ? { me: me.value } : {}),
      }
      await setPreference(STORAGE_KEY, JSON.stringify(session))
    } else {
      await removePreference(STORAGE_KEY)
    }
  }

  async function adopt(tokens: TokenResponse) {
    accessToken.value = tokens.access_token
    if (tokens.refresh_token) refreshToken.value = tokens.refresh_token
    await persist()
  }

  async function clear() {
    accessToken.value = null
    refreshToken.value = null
    me.value = null
    await persist()
  }

  // One refresh at a time, shared by every request that hit a 401.
  let refreshing: Promise<boolean> | null = null
  function refresh(): Promise<boolean> {
    if (!refreshToken.value) return Promise.resolve(false)
    refreshing ??= (async () => {
      try {
        const tokens = await api('/auth/refresh', {
          method: 'POST',
          body: { refresh_token: refreshToken.value },
          schema: TokenResponse,
          auth: false,
        })
        await adopt(tokens)
        return true
      } catch (err) {
        // Only a definite rejection ends the session; being offline does not.
        if (err instanceof ApiRequestError && err.status === 401) await clear()
        return false
      } finally {
        refreshing = null
      }
    })()
    return refreshing
  }

  setTokenProvider({ accessToken: () => accessToken.value, refresh })

  async function loadMe() {
    me.value = await api('/me', { schema: Me })
    await persist()
  }

  /** Restore a saved session on app start. Safe to call more than once. */
  async function init() {
    if (ready.value) return
    try {
      const raw = await getPreference(STORAGE_KEY)
      if (raw) {
        const saved = JSON.parse(raw) as StoredSession
        accessToken.value = saved.access_token
        refreshToken.value = saved.refresh_token
        const cachedMe = Me.safeParse(saved.me)
        if (cachedMe.success) me.value = cachedMe.data
        await loadMe().catch(async (err: unknown) => {
          if (err instanceof ApiRequestError && err.status === 401) await clear()
          // Offline: stay signed in with what we have, including the cached profile.
        })
      }
    } catch {
      await clear()
    } finally {
      ready.value = true
    }
  }

  async function login(body: LoginBody) {
    await adopt(
      await api('/auth/login', { method: 'POST', body, schema: TokenResponse, auth: false }),
    )
    await loadMe()
  }

  async function register(body: RegisterBody) {
    await adopt(
      await api('/auth/register', { method: 'POST', body, schema: TokenResponse, auth: false }),
    )
    await loadMe()
  }

  /** Ask for a reset email. The API answers the same whether or not the account exists. */
  async function forgotPassword(email: string) {
    await api('/auth/forgot-password', { method: 'POST', body: { email }, auth: false })
  }

  /** Set a new password from an emailed link. Signs in, and out everywhere else. */
  async function resetPassword(token: string, password: string) {
    await adopt(
      await api('/auth/reset-password', {
        method: 'POST',
        body: { token, password },
        schema: TokenResponse,
        auth: false,
      }),
    )
    await loadMe()
  }

  /** Change the password. Other devices are signed out; this one gets a new session. */
  async function changePassword(currentPassword: string, newPassword: string) {
    await adopt(
      await api('/me/password', {
        method: 'POST',
        body: { current_password: currentPassword, new_password: newPassword },
        schema: TokenResponse,
      }),
    )
  }

  /** Signed up on klunkers.bike: swap the website's handoff token for a session here. */
  async function redeemHandoff(handoff: string) {
    await adopt(
      await api('/auth/handoff/redeem', {
        method: 'POST',
        body: { handoff },
        schema: TokenResponse,
        auth: false,
      }),
    )
    await loadMe()
  }

  /**
   * Sign in with a passkey (docs/passkeys.md): the device offers whichever it
   * holds for Klunkers, and the server gives the same tokens as a password.
   * Resolves false when the rider closed the sheet, which is not an error.
   */
  async function signInWithPasskey(): Promise<boolean> {
    const { challenge_id, options } = await api('/auth/passkeys/login/options', {
      method: 'POST',
      schema: PasskeyOptions,
      auth: false,
    })
    let response: Record<string, unknown>
    try {
      response = await getPasskey(options)
    } catch (err) {
      if (err instanceof PasskeyCancelled) return false
      throw err
    }
    await adopt(
      await api('/auth/passkeys/login', {
        method: 'POST',
        body: { challenge_id, response },
        schema: TokenResponse,
        auth: false,
      }),
    )
    await loadMe()
    // This device has one, so a password sign-in here later need not offer one.
    if (me.value) markPasskeyHere(me.value.id)
    return true
  }

  async function listPasskeys(): Promise<Passkey[]> {
    return (await api('/me/passkeys', { schema: PasskeyList })).passkeys
  }

  /** Add a passkey for this rider on this device. Null when the sheet was closed. */
  async function addPasskey(name: string): Promise<Passkey | null> {
    const { challenge_id, options } = await api('/me/passkeys/register/options', {
      method: 'POST',
      schema: PasskeyOptions,
    })
    let response: Record<string, unknown>
    try {
      response = await createPasskey(options)
    } catch (err) {
      if (err instanceof PasskeyCancelled) return null
      throw err
    }
    const added = await api('/me/passkeys/register', {
      method: 'POST',
      body: { challenge_id, name, response },
      schema: Passkey,
    })
    if (me.value) {
      markPasskeyHere(me.value.id)
      me.value = { ...me.value, passkeys_declined: false }
      await persist()
    }
    return added
  }

  /**
   * Whether to offer a passkey just after a password sign-in: the device can
   * make one, this rider hasn't made one here, and they haven't said they'd
   * rather keep their password.
   */
  async function shouldOfferPasskey(): Promise<boolean> {
    if (!me.value || me.value.passkeys_declined || hasPasskeyHere(me.value.id)) return false
    return passkeysSupported()
  }

  /** "I'll stick with passwords", for this rider on every device. */
  async function declinePasskeys(): Promise<void> {
    await api('/me/passkeys/decline', { method: 'POST' })
    if (me.value) {
      me.value = { ...me.value, passkeys_declined: true }
      await persist()
    }
  }

  async function removePasskey(id: string): Promise<void> {
    await api(`/me/passkeys/${id}`, { method: 'DELETE' })
  }

  async function logout() {
    const token = refreshToken.value
    await clear()
    if (token) {
      await api('/auth/logout', {
        method: 'POST',
        body: { refresh_token: token },
        auth: false,
      }).catch(() => {})
    }
  }

  async function setUnits(value: UnitsPreference) {
    me.value = await api('/me', { method: 'PATCH', body: { units: value }, schema: Me })
    await persist()
  }

  return {
    accessToken,
    me,
    ready,
    isSignedIn,
    units,
    userId,
    init,
    login,
    register,
    logout,
    forgotPassword,
    redeemHandoff,
    resetPassword,
    signInWithPasskey,
    listPasskeys,
    addPasskey,
    shouldOfferPasskey,
    declinePasskeys,
    removePasskey,
    changePassword,
    refresh,
    setUnits,
    loadMe,
  }
})
